import React from "react";
import { useEffectOnce } from "react-use";

import { Box, Modal as MuiModal, css, styled } from "@mui/material";

import { scrollBarSxStyles } from "@/shared/styles";
import { skipForwardProp } from "@/utils/skipForwardProp";

import { useGlobalModalContext } from "./context";

const StyledMuiModal = styled(MuiModal, {
  shouldForwardProp: skipForwardProp(["darkerBackdrop"]),
})<{ darkerBackdrop?: boolean }>(
  ({ darkerBackdrop }) => css`
    ${!!darkerBackdrop
      ? css`
          .MuiBackdrop-root {
            background-color: rgba(0, 0, 0, 0.8);
          }
        `
      : ""}
  `,
);

type StyledBoxProps = {
  disableScrollActions: boolean;
};

const StyledBox = styled(Box, {
  shouldForwardProp: skipForwardProp(["disableScrollActions"]),
})<StyledBoxProps>(({ disableScrollActions }) => {
  return css`
    max-height: 100vh;
    max-height: 100dvh;

    -ms-overflow-style: none; /* IE and Edge */
    scrollbar-width: none; /* Firefox */
    &::-webkit-scrollbar {
      display: none; /* Chrome, Safari and Opera */
    }

    touch-action: ${disableScrollActions ? "none" : "auto"};
    &::-webkit-scrollbar {
      display: none !important;
    }
  `;
});

export type GlobalModalPropTypes = {
  darkerBackdrop?: boolean;
  disableScrollActions?: boolean;
  onOkClick?: () => void;
  disableBackGroundClick?: boolean;
};

export const GlobalModal: React.FC = () => {
  const { openedModals, removeOne, removeAll } = useGlobalModalContext();

  useEffectOnce(() => {
    removeAll();
  });
  return (
    <>
      {openedModals.map(
        ({ ModalComponent, modalProps, globalModalProps }, i) => {
          const {
            disableBackGroundClick,
            onOkClick,
            darkerBackdrop,
            disableScrollActions = false,
            ...restGlobalModalProps
          } = globalModalProps;

          return (
            <StyledMuiModal
              className="flex items-center justify-center"
              key={i}
              open={true}
              disableScrollLock
              darkerBackdrop={darkerBackdrop}
              onClose={(_, reason) => {
                if (reason === "backdropClick" && disableBackGroundClick)
                  return;
                // FIXME: RW-148 onOkClick should not be called here. need to check all modals that use this
                // and pass onOkClick directly to modal. remove from GlobalModal props
                (onOkClick || removeOne)();
              }}
              slotProps={{
                backdrop: {
                  onClick: (e) => {
                    if (disableBackGroundClick) {
                      e.stopPropagation();
                    }
                  },
                },
              }}
              {...restGlobalModalProps}
            >
              <StyledBox
                className="overflow-y-auto outline-none"
                sx={scrollBarSxStyles}
                disableScrollActions={disableScrollActions}
              >
                {/* eslint-disable-next-line */}
                <ModalComponent {...(modalProps as any)} />
              </StyledBox>
            </StyledMuiModal>
          );
        },
      )}
    </>
  );
};
